import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'

/**
 * Pointed chevron chip used in deal pipelines.
 * Variants map to Figma "Type" variants:
 *   default / hover / selected — filled-purple/indigo states (no count badge)
 *   new                        — red "Новая" with count badge
 *   contact / negotiation /
 *   meeting / contract         — pastel deal-stage chips with count badge
 */
const dealStageVariants = cva(
	'relative inline-flex items-stretch transition-colors duration-150 rounded-l-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-(--color-ring) focus-visible:ring-offset-2',
	{
		variants: {
			type: {
				default:     'text-(--color-muted) hover:text-(--color-brand-light)',
				hover:       'text-(--color-brand-light)',
				selected:    'text-(--color-brand)',
				new:         'text-(--color-destructive-hover)',
				contact:     'text-violet-200',
				negotiation: 'text-blue-200',
				meeting:     'text-orange-200',
				contract:    'text-green-200',
			},
		},
		defaultVariants: { type: 'default' },
	}
)

const dealStageLabelVariants = cva('text-base leading-6 text-center whitespace-nowrap', {
	variants: {
		type: {
			default:     'text-(--color-muted-foreground)',
			hover:       'text-(--color-muted-foreground)',
			selected:    'text-(--color-background)',
			new:         'text-(--color-primary-foreground)',
			contact:     'text-(--color-foreground)',
			negotiation: 'text-(--color-foreground)',
			meeting:     'text-(--color-foreground)',
			contract:    'text-(--color-foreground)',
		},
	},
	defaultVariants: { type: 'default' },
})

const dealStageCountVariants = cva(
	'inline-flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-[600] leading-4 whitespace-nowrap',
	{
		variants: {
			type: {
				default:     'hidden',
				hover:       'hidden',
				selected:    'hidden',
				new:         'bg-white/40 text-(--color-primary-foreground)',
				contact:     'bg-white/50 text-(--color-muted-foreground)',
				negotiation: 'bg-white/50 text-(--color-muted-foreground)',
				meeting:     'bg-white/50 text-(--color-muted-foreground)',
				contract:    'bg-white/50 text-(--color-muted-foreground)',
			},
		},
		defaultVariants: { type: 'default' },
	}
)

export type DealStageType =
	| 'default'
	| 'hover'
	| 'selected'
	| 'new'
	| 'contact'
	| 'negotiation'
	| 'meeting'
	| 'contract'

export interface DealStageElementProps
	extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'>,
		VariantProps<typeof dealStageVariants> {
	/** Chip label */
	label: string
	/** Optional count pill — shown on `new` and pastel variants */
	count?: number
	/** Forces the hover-only action button ("+") to be visible */
	showAction?: boolean
	/** Custom action button content (defaults to a "+" icon) */
	action?: React.ReactNode
	/** Accessible label for the action button */
	actionLabel?: string
	onAction?: (e: React.MouseEvent<HTMLButtonElement>) => void
}

const Tip = () => (
	<span
		aria-hidden
		className="self-start shrink-0"
		style={{
			width: 0,
			height: 0,
			borderTop: '18px solid transparent',
			borderBottom: '18px solid transparent',
			borderLeft: '14px solid currentColor',
		}}
	/>
)

const PlusIcon = () => (
	<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
		<path d="M12 5v14M5 12h14"/>
	</svg>
)

const DealStageElement = React.forwardRef<HTMLButtonElement, DealStageElementProps>(
	(
		{
			className,
			type = 'default',
			label,
			count,
			showAction = false,
			action,
			actionLabel = 'Add',
			onAction,
			...props
		},
		ref
	) => {
		const hasCount = typeof count === 'number' && type !== 'default' && type !== 'hover' && type !== 'selected'
		return (
			<div className={cn('group/stage relative flex', className)}>
				<button
					ref={ref}
					type="button"
					className={cn(dealStageVariants({ type }), 'w-full cursor-pointer')}
					{...props}
				>
					<span className="flex h-9 w-full items-center justify-center gap-2 rounded-l-md bg-current py-1.5">
						<span className={dealStageLabelVariants({ type })}>{label}</span>
						{hasCount && (
							<span className={dealStageCountVariants({ type })}>{count}</span>
						)}
					</span>
					<Tip />
				</button>
				<button
					type="button"
					onClick={onAction}
					aria-label={actionLabel}
					className={cn(
						'absolute right-[-16px] top-1/2 -translate-y-1/2 flex items-center justify-center p-0.5 rounded-md bg-(--color-secondary) text-(--color-secondary-foreground) cursor-pointer',
						'transition-opacity duration-150',
						showAction
							? 'opacity-100'
							: 'opacity-0 pointer-events-none'
					)}
				>
					{action ?? <PlusIcon />}
				</button>
			</div>
		)
	}
)

DealStageElement.displayName = 'DealStageElement'
export { DealStageElement, dealStageVariants }
